import { onAuthStateChanged, signInWithCustomToken, User } from "firebase/auth";
import { useState, useEffect } from 'react';
import { auth } from "../firebase";
import { useNavigate } from "react-router-dom";
import { ProfileType } from "../models/profileType";
import { ProfileService } from "../services/profile/profile";

export const useAuthRouting = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<ProfileType | null>(null);
  const isAuthReady = useAuthReady();

  useEffect(() => {
    if (!isAuthReady) return;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        let localProfile = profile
        if (!profile || profile.id != user.uid) {
          localProfile = await ProfileService.getProfile(user.uid)
          setProfile(localProfile)
        }
        if (localProfile) {
          if (!user.emailVerified && localProfile.profileType !== "staff") {
            navigate('/verify-success')
          }
        } else {
          navigate('/verify-success')
        }
        console.log("User is already signed in:", user);
      }
      else {
        navigate("/login");
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [navigate, isAuthReady]);
};

export const useSignedInState = () => {
  const [signedIn, setSignedIn] = useState(true);
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log("User is already signed in:", user);
        setSignedIn(true)
      } else {
        setSignedIn(false)
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);
  return signedIn;
};

export interface signedInUser {
  user: User | null;
  profile: ProfileType | null;
}

export const useSignedInUserState = (): signedInUser => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<ProfileType | null>(null);
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        console.log("User is already signed in:", user);
        setUser(user);
        const profile = await ProfileService.getProfile(user.uid)
        setProfile(profile);
      } else {
        setUser(null)
      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [user]);
  return { user, profile };
};

export const useAuthReady = (timeoutMs: number = 5000) => {
  const [isAuthReady, setIsAuthReady] = useState(false);

  useEffect(() => {
    let resolved = false;

    const handleFlutterAuth = async () => {
      resolved = true;
      const token = localStorage.getItem("firebase_custom_token");
      if (!token) {
        setIsAuthReady(true);
        return;
      }

      try {
        await signInWithCustomToken(auth, token);
        console.log("✅ Logged in with Flutter custom token");
      } catch (err) {
        console.error("❌ Failed to login with Flutter custom token", err);
      } finally {
        setIsAuthReady(true);
      }
    };

    // Listen for Flutter event
    window.addEventListener("flutter-auth-ready", handleFlutterAuth);

    // Fallback: if Flutter never dispatches, continue anyway
    const timer = setTimeout(() => {
      if (!resolved) {
        console.log("⏱️ No flutter-auth-ready, continuing in web mode");
        setIsAuthReady(true);
      }
    }, timeoutMs);

    return () => {
      window.removeEventListener("flutter-auth-ready", handleFlutterAuth);
      clearTimeout(timer);
    };
  }, [timeoutMs]);

  return isAuthReady;
};
